import React, { useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import styled, { css } from 'styled-components';
import Icon from '../Icon';
import Colors from 'constants/Colors';
import Label from './Label';
import ValidationError from './ValidationError';
import IMask from 'imask';
import { IMaskMixin } from 'react-imask';

import Spinner, { SpinnerSize } from '../Spinner';
import { FiraSansBold, FontWeight, Text } from '../Text';
import Button from '../ButtonForCalendar';
import { Breakpoints } from "../../constants/Breakpoints";
import { mediaHelper } from "../../utils/src";
import { debounce } from 'lodash';

export const InputSize = {
  SMALL: 1,
  NORMAL: 2
};

const propTypes = {
  type: PropTypes.string,
  placeholder: PropTypes.string,
  mask: PropTypes.any,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  leftRender: PropTypes.func,
  rightRender: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  format: PropTypes.func,
  parse: PropTypes.func,
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  isRequired: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onPressEnter: PropTypes.func,
  fieldProps: PropTypes.any,
  smallInput: PropTypes.bool,
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  inverseBlue: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(InputSize)),
  validation: PropTypes.bool,
  valueCalendar: PropTypes.any,
  originalSize: PropTypes.bool,
  customValue: PropTypes.string,
};

const defaultProps = {
  type: 'text',
  mask: null,
  placeholder: '',
  leftIcon: null,
  rightIcon: null,
  leftRender: null,
  rightRender: null,
  label: null,
  format: value => value,
  parse: value => value,
  isLoading: false,
  isRequired: false,
  tabIndex: 0,
  onChange: value => value,
  onBlur: value => value,
  onFocus: value => value,
  onPressEnter: value => value,
  fieldProps: {},
  smallInput: false,
  readOnly: false,
  inline: false,
  inverseBlue: false,
  size: InputSize.NORMAL,
  validation: true,
  valueCalendar: null,
  originalSize: false,
  customValue: null,
};

// Mixins
const errorInputMixin = css`
  box-shadow: 0 0 0 3px ${Colors.DANGER};

  &:focus {
    box-shadow: 0 0 0 3px ${Colors.DANGER};
  }
`;

// Components
export const InputContainer = styled.div`
  width: 100%;
  position: relative;
  height: 42px;
  box-sizing: border-box;
  border-radius: 4px;

  display: flex;
  align-items: center;
  justify-content: center;
  background: white;




  box-shadow: 0 0 0 1px ${Colors.GRAY[3]};

  transition: box-shadow 0.15s ease;
  margin-bottom: 10px;
  ${({ isFocused }) =>
    isFocused &&
    css`
      box-shadow: 0 0 0 3px ${Colors.SECONDARY};
      
      ${Input} {
        background: ${Colors.GRAY[5]}
        box-shadow: inset 0px 2px 6px rgba(0, 0, 0, 0.2);
      }
    `};

  ${({ warningByHiddenQuestions }) =>
    warningByHiddenQuestions &&
    css`
      box-shadow: 0 0 0 1px #FDC830;
    `};

  ${({ refusedByHiddenQuestions }) =>
    refusedByHiddenQuestions &&
    css`
      box-shadow: 0 0 0 1px #DB0F16;
    `};

  ${({ inline }) =>
    inline &&
    css`
      width: 60%;
      min-width: 60%
    `};

  ${({ meta }) => (meta && meta.error ? errorInputMixin : "")};

  @media screen and (min-width: ${Breakpoints.md}) {
    margin-bottom: 0;
  }
`;

export const Input = styled.input`
  scroll-margin: 40px;
  width: ${({ smallInput }) => (smallInput ? '40px' : '100%')};
  min-width: 0;
  padding: 4px;
  border: 0;
  background: white;
  appearance: none;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  outline: none;
  box-sizing: border-box;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansBold};

  transition: background 0.15s ease, box-shadow 0.15s ease;

  height: 42px;
  min-height: unset;

  @media screen and (min-width: ${Breakpoints.sm}) {
    height: 42px;
  }

  &::placeholder {
    color: ${Colors.GRAY[3]};
  }

  &[disabled] {
    background-color: ${Colors.GRAY[4]};
    color: ${Colors.GRAY[0]};
  }
`;

export const IconButton = styled(Button).attrs({
  type: 'button',
  tabIndex: -1
})`
  padding: 0;
  height: 24px;

  ${mediaHelper.sm`
    height: unset;
  `}

  ${({ disabled }) => disabled && css`
    opacity: 0.3;
    pointer-events: none;
    `
  }

  & ${Icon} {
    font-size: 14px;
    color: ${Colors.GRAY[3]};
    width: 30px;
    height: 30px;
    background-size: 100%;
    margin: 3px;

    ${mediaHelper.sm`
      width: 30px;
      height: 30px;
      margin: 0 12px;
    `}
  }
`;

const InputSpinner = styled(Spinner)`
  margin: 0 12px;
`;

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 15px;

  ${({ inline }) =>
    inline &&
    css`
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
    `}

    ${({ disabled }) =>
    disabled &&
    css`
        opacity: 0.3;
    `}
`;

export const Option = styled.li`
  list-style: none;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${Colors.GRAY[3]};

  ${({ selected }) =>
    selected &&
    css`
      background-color: ${Colors.Alpha(Colors.SECONDARY, 0.7)};
    `}

  &:hover {
    background-color: ${Colors.GRAY[4]};
  }
`;

export const OptionsContainer = styled.ul`
  position: absolute;
  z-index: 2000;
  top: 40px;
  left: 0px;
  max-height: 300px;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-left: 3px solid ${Colors.SECONDARY};
  border-right: 3px solid ${Colors.SECONDARY};
  border-bottom: 3px solid ${Colors.SECONDARY};
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  
  @media (max-width: 768px) {
    width: 93%;
  }
`;

const MaskedInput = IMaskMixin(({ inputRef, ...props }) => (
  <Input {...props} ref={inputRef} />
));

const InputField = ({
  type,
  placeholder,
  disabled,
  readOnly,
  mask,
  leftIcon,
  leftIconClick,
  leftIconText,
  rightIcon,
  rightIconClick,
  leftRender,
  rightRender,
  isLoading,
  label,
  field,
  format,
  parse,
  size,
  setLabelMinHeight,
  fieldProps,
  isRequired,
  tabIndex,
  form: { setFieldValue, setFieldTouched, touched, errors, setFieldError },
  validationMinHeight,
  hideLabelAsterisk,
  smallInput,
  alignCenter,
  inline,
  validation,
  inverseBlue,
  onInputRef,
  onChange,
  onBlur,
  onFocus,
  onPressEnter,
  valueCalendar,
  originalSize,
  customValue,
  onSearchSelect,
  extraLabelTop,
  key,
  canGenerate,
  formatTextHtml,
  question,
  initSearchList,
  warningByHiddenQuestions,
  refusedByHiddenQuestions,
  searchRequest = (searchValue, question, setSearchList) => [],
  debounceSearchRequest,
  extraExclamationWarning,
  searchRequestDebounceMS,
  questionExtraContentRight,
  enableSearch,
  ...props
}) => {
  const inputRef = useRef(null);
  const selectionStart = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const firstChange = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchList, setSearchList] = useState(initSearchList || []);

  useEffect(() => {
    setSearchList(initSearchList || []);
  }, [initSearchList])

  const debounceOnSearchRequest = debounceSearchRequest 
      ? useRef(debounce((searchValue, question) => searchRequest(searchValue, question, setSearchList), searchRequestDebounceMS || 250)).current 
      : debounce((searchValue, question) => searchRequest(searchValue, question, setSearchList), searchRequestDebounceMS || 250)

  var touchedList;
  var touchedItem;
  var errorList;
  var errorItem;

  const metaFields = field?.name?.split('.');
  if (metaFields?.length == 3) {
    touchedList = touched[metaFields[0]]
    if (touchedList) {
      touchedItem = touchedList[parseInt(metaFields[1])]
    }
    errorList = errors[metaFields[0]]
    if (errorList) {
      errorItem = errorList[parseInt(metaFields[1])]
    }
  }

  const meta = {
    touched: metaFields?.length == 3 && touchedItem ? touchedItem[metaFields[2]] || touched[field?.name] : touched[field?.name],
    error: errors[field?.name] || '',
    isFocused: isFocused
  };

  const _onFocus = () => {
    setIsFocused(true);
  };

  const _onBlur = () => {
    setIsFocused(false);
  };

  const selectedResponse = useRef(true);

  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [])

  useEffect(() => {
    setTimeout(() => !firstChange.current ? firstChange.current = true : false, 500)
  }, [])

  if (!mask) mask = { mask: /.*/ }
  let inputMask = mask?.mask === 'Number' || mask?.mask === 0 ? Number : mask?.mask;

  return (
    <Wrapper
      inline={inline}
      //disabled={disabled}
      inverse={inverseBlue}
      {...props}
    >
      {(extraLabelTop) && (
        <Label
          meta={meta}
          weight={FontWeight.SemiBold}
          setMinHeight={setLabelMinHeight}
          hideLabelAsterisk={true}
          style={{ marginTop: 20, marginBottom: 20, marginLeft: inline ? '36%' : 0 }}
          bold
        >
          {extraLabelTop}
        </Label>
      )}

      {(label || setLabelMinHeight) && (
        <Label
          meta={meta}
          width={inline ? 35 : null}
          isRequired={isRequired}
          weight={FontWeight.SemiBold}
          setMinHeight={setLabelMinHeight}
          inline={inline}
          inverse={inverseBlue}
          hideLabelAsterisk={hideLabelAsterisk}
        >
          {label}
        </Label>
      )}
      <div style={{ width: '100%', display: 'flex', gap: '12px'}}>
      <InputContainer
        meta={meta}
        isFocused={meta.isFocused}
        inline={inline}
        warningByHiddenQuestions={warningByHiddenQuestions}
        refusedByHiddenQuestions={refusedByHiddenQuestions}
      >
        {leftIconText && (
          <p style={{ margin: 0 }}>{leftIconText}</p>
        )}
        <MaskedInput
          ref={ref => {
            inputRef.current = ref;
            if (onInputRef) onInputRef(ref);
          }}
          autoComplete="off"
          name={field.name}
          key={`${key}-input`}
          lazy={true}
          mask={inputMask}
          blocks={mask?.blocks}
          scale={mask?.scale}
          signed={mask?.signed}
          padFractionalZeros={mask?.padFractionalZeros}
          radix={mask?.radix}
          mapToRadix={mask?.mapToRadix || []}
          thousandsSeparator={mask?.thousandsSeparator}
          min={mask?.min}
          max={mask?.max}
          size={size}
          type={type}
          dispatch={(appended, dynamicMasked) => {
            const value = String(dynamicMasked.value + appended).replace(/\D/g, '');

            return dynamicMasked.compiledMasks.find((m, index) => {
              return value.length <= 11 ? index === 0 : index === 1
            })
          }}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={tabIndex}
          onKeyDown={e => {
            if (e.key.toLowerCase() === 'enter') {
              e.preventDefault();
              e.stopPropagation();
              onPressEnter(field.value);
              rightIconClick && rightIconClick();
            }
          }}
          onFocus={() => {
            setIsOpen(true);
            if (question) debounceOnSearchRequest(field.value, question)

            if (!canGenerate) {
              if (!selectedResponse.current) {
                setFieldValue(field.name, '');
              }
            }

            onFocus(field.value);
            _onFocus();
          }}
          onBlur={() => {
            if (!canGenerate) {
              if (!selectedResponse.current) {
                setFieldValue(field.name, '');
              }
            }
            setIsOpen(false)
            /* setTimeout(()=>{ },500) */
            onBlur(field.value, this);
            _onBlur();

            if (!meta.touched) {
              setFieldTouched(field.name, true);
            }
          }}
          smallInput={smallInput}
          inverseBlue={inverseBlue}
          alignCenter={alignCenter}
          value={String(field.value || '')}
          unmask={true}
          onChange={(event) => {
            if (event.target.value === '' && [1571, 1575, 1748].includes(question.idQuestion)) debounceOnSearchRequest(' ', question);
            if (mask) return;
            onChange(event.target.value);
            setFieldValue(field.name, event.target.value);
          }}
          onAccept={(value, mask) => {
            if (meta.error) setFieldError(field.name, '')

            if (!firstChange.current) return firstChange.current = true;
            if (!mask) return;
            onChange(value)
            setFieldValue(field.name, value);
            if (question && isOpen) debounceOnSearchRequest(value, question)

            selectedResponse.current = (value == field.value);
          }}
        />

        {extraExclamationWarning && <Icon name='exclamation-red' title='aviso' />}

        {!rightIcon ?
          <IconButton  disabled={!enableSearch && disabled} onClick={rightIconClick}> 
            <Icon name={"search"} />
          </IconButton>
          :
          <IconButton disabled={!enableSearch && disabled} onClick={rightIconClick}>
            <Icon name={rightIcon} />
          </IconButton>
        }

        {rightRender && rightRender()}
        {isLoading && <InputSpinner size={SpinnerSize.SMALL} />}
        {isOpen && searchList?.length > 0 && (
          <OptionsContainer>
            {searchList.map((x, i) => (
              <Option key={`opt-${x.value}-${i}`}
                onMouseDown={() => {
                  setIsOpen(false);
                  selectedResponse.current = true;
                  !formatTextHtml && setFieldValue(field.name, x.value);
                  !formatTextHtml && onChange(x.value);
                  onSearchSelect(x);
                  setSearchList([])
                }}
              >
                {formatTextHtml ?
                  <Text dangerouslySetInnerHTML={{ __html: x.value }} />
                  :
                  <Text weight={FontWeight.Regular} >
                    {x.value}
                  </Text>}
              </Option>
            ))}
          </OptionsContainer>
        )}
      </InputContainer>
      {questionExtraContentRight && questionExtraContentRight()}
      </div>
      {validation && (
        <ValidationError
          style={{ minWidth: '100%', position: 'absolute', bottom: -38, paddingRight: inline ? 18 : 0 }}
          meta={meta}
          validationMinHeight={validationMinHeight}
        />
      )}
    </Wrapper>
  );
};

InputField.propTypes = propTypes;
InputField.defaultProps = defaultProps;

export default InputField;